/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CitySection } from '../../models/cart-state.model';
import { Router } from '@angular/router';
import { ItineraryService } from '../../../itinerary.service';
import { HotelService } from '../../../modals/modal-hotel/hotel.service';
import { TransportsService } from '../../../modals/modal-transports/transports.service';
import { ModalsComponent } from '../../../modals/modals.component';
import { ConfirmationModalComponent } from 'app/shared/modals/confirmation-modal/confirmation-modal.component';
import { CancelModalComponent } from 'app/shared/modals/cancel-modal/cancel-modal.component';
import { CurrencyConverterPurePipe } from 'app/shared/currency-converter-pure.pipe';
import { concat, of, Subject, Observable } from 'rxjs';
import { concatMap, delay, takeUntil, take, filter, map } from 'rxjs/operators';
import { NotificationService } from 'app/shared/notifications/notification.service';

@Component({
  selector: 'usb-cart-sidebar',
  templateUrl: './cart-sidebar.component.html',
  styleUrls: ['./cart-sidebar.component.scss'],
  providers: [CurrencyConverterPurePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartSidebarComponent implements OnChanges, OnDestroy {
  bsModalRef?: BsModalRef;
  config = {
    animated: true,
    keyboard: true,
    backdrop: 'static',
    ignoreBackdropClick: true,
  };

  // Skeleton state
  isLoading = true;
  loadingItemIds = new Set<string>();
  previousItemIds = new Set<string>();
  readonly SKELETON_ITEMS = [0, 1, 2]; // 3 skeleton items for initial load


  // RxJS cleanup
  protected destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private _router: Router,
    private itineraryService: ItineraryService,
    private hotelService: HotelService,
    private transportsService: TransportsService,
    private modalService: BsModalService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private currency: CurrencyConverterPurePipe,
  ) {}

@Input() _actualItinerary: any;
  @Input() styleSidebar: any;
  @Input() currencyAgency: any;
  @Input() currencies: any;
  @Input() clientsSettingAppBooking: any;
  @Input() currentCategory: string;
  @Input() permissions: any;
  @Output() itemRemove = new EventEmitter<any>();

  cities: CitySection[] = [];
  insuranceItems: any[] = [];

  // Track which duplicate pairs we already warned about, to avoid
  // spamming notification on every ngOnChanges cycle.
  private notifiedDuplicateKeys = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['_actualItinerary']) {
      const prevIds = new Set(this.previousItemIds);
      this.buildCartCities();
      this.warnNewDuplicates();

      // Collect current item IDs
      this.previousItemIds = new Set<string>();
      this.cities.forEach(city => {
        city.items.forEach(dg => {
          dg.items.forEach(item => {
            this.previousItemIds.add(this.getItemTrackId(item));
          });
        });
      });

      if (this.isLoading) {
        // First load: delay so the skeleton actually renders before data appears
        setTimeout(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }, 600);
      } else {
        // Detect newly added items → show skeleton briefly
        let hasNew = false;
        this.previousItemIds.forEach(id => {
          if (!prevIds.has(id)) {
            this.loadingItemIds.add(id);
            hasNew = true;
            setTimeout(() => {
              this.loadingItemIds.delete(id);
              this.cdr.detectChanges();
            }, 700);
          }
        });
        if (hasNew) {
          this.cdr.detectChanges();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // =====================
  // Duplicate detection
  // =====================

  /**
   * Flatten the grouped cart structure into a list of individual cart items.
   * For hotels/transports/tickets grouped by hotelCode/providerId-productId,
   * we expand the inner rooms/transfers/ticketItems so each leaf is comparable.
   */
  private flattenCartItems(): any[] {
    const out: any[] = [];
    if (!this.cities) {
return out;
}

    for (const city of this.cities) {
      for (const dateGroup of city.items) {
        for (const item of dateGroup.items as any[]) {
          // Hotels: group contains `rooms[]`
          if (item.categoryId === 1 && Array.isArray(item.rooms) && item.rooms.length) {
            for (const room of item.rooms) {
              out.push({
                ...room,
                categoryId: 1,
                // tag with the group id so we can dedupe per room, not per hotel
                _groupId: item.hotelCode || item.title,
              });
            }
            continue;
          }
          // Transports: group contains `transfers[]` (ida/vuelta)
          if (item.categoryId === 8 && Array.isArray(item.transfers) && item.transfers.length) {
            for (const transfer of item.transfers) {
              out.push({
                ...transfer,
                categoryId: 8,
                _groupId: `${item.providerId}-${item.productId}-${transfer.firstWay ?? 'x'}`,
              });
            }
            continue;
          }
          // Tickets: group contains `ticketItems[]`
          if (item.ticketItems && Array.isArray(item.ticketItems) && item.ticketItems.length) {
            for (const t of item.ticketItems) {
              out.push({
                ...t,
                categoryId: item.subCategoryId === 9 ? 9 : item.categoryId,
                _groupId: `${item.providerId}-${item.productId}-${t.id ?? Math.random()}`,
              });
            }
            continue;
          }
          // Insurance
          if (item.categoryId === 10) {
            out.push({ ...item, categoryId: 10 });
            continue;
          }
          // Tours / experiences / rent-a-car / standalone items
          out.push(item);
        }
      }
    }
    return out;
  }

  /**
   * Returns a stable key identifying "which product on which date" this item
   * represents. Two items share a key when they are the same product on the
   * same date.
   */
  private getDuplicateKey(item: any): string | null {
    const productId = item.productId ?? item.product_id;
    const providerId = item.providerId ?? item.provider_id;
    if (!productId) {
return null;
}

    // Date resolution per category. We compare on the service date:
    //  - hotel / insurance / rent-a-car: arrivalDate (check-in)
    //  - tours / tickets / transport: arrivalDate (single-day service)
    //    (for transports, firstWay=1 is ida, firstWay=2 is vuelta, so each leg
    //    counts as its own date → the firstWay is part of the key)
    const arrival = (item.arrivalDate || '').substring(0, 10);
    const categoryId = item.categoryId ?? item.category_id;

    if (categoryId === 8) {
      return `${categoryId}|${providerId ?? ''}|${productId}|fw${item.firstWay ?? 0}|${arrival}`;
    }
    return `${categoryId}|${providerId ?? ''}|${productId}|${arrival}`;
  }

  /**
   * Status helpers — we treat an item as "active" when it is in
   * cotización (1) or reservado (2). Other statuses (cancelled, denied,
   * paid, completed…) do NOT count as a duplicate trigger.
   */
  private isActiveStatus(item: any): boolean {
    const sid = item.statusId ?? item.status_id;
    return sid === 1 || sid === 2;
  }

  /**
   * Public: returns the first item in the cart that is a duplicate of `item`
   * (same productId + providerId + same date) and that is in an active
   * status (1 = cotización, 2 = reservado). Returns `null` if no duplicate.
   */
  getDuplicateOf(item: any): any | null {
    if (!item) {
return null;
}
    const key = this.getDuplicateKey(item);
    if (!key) {
return null;
}

    // Identify the current item's own groupId so we don't match against
    // the same room/transfer/ticket that originated this grouped item.
    const ownGroupId =
      item._groupId ??
      (item.rooms ? (item.hotelCode || item.title) : null) ??
      item.id;

    const all = this.flattenCartItems();
    for (const other of all) {
      if (other === item) {
continue;
}
      const otherKey = this.getDuplicateKey(other);
      if (otherKey !== key) {
continue;
}
      if (!this.isActiveStatus(other)) {
continue;
}
      // Skip the same leaf (e.g. the same room inside the same hotel group)
      if (ownGroupId && other._groupId && other._groupId === ownGroupId) {
continue;
}
      return other;
    }
    return null;
  }

  /**
   * Template helper: true when this item has an active duplicate in the cart.
   */
  isDuplicated(item: any): boolean {
    return this.getDuplicateOf(item) !== null;
  }

  /**
   * Returns the human-readable status name of the duplicate (e.g. "Cotización"
   * or "Reservado") so the banner can show it.
   */
  getDuplicateStatusName(item: any): string {
    const dup = this.getDuplicateOf(item);
    if (!dup) {
return '';
}
    const sid = dup.statusId ?? dup.status_id;
    if (sid === 2) {
return 'Reservado';
}
    if (sid === 1) {
return 'Cotización';
}
    return dup.statusName || dup.status || 'Activo';
  }

  /**
   * Walks the current cart, finds active duplicate pairs that have NOT been
   * notified yet, and fires a single notificationService.warning per pair.
   */
  private warnNewDuplicates(): void {
    if (!this.notificationService) {
return;
}
    const all = this.flattenCartItems();
    const currentDuplicateKeys = new Set<string>();

    for (const item of all) {
      if (!this.isActiveStatus(item)) {
continue;
}
      const key = this.getDuplicateKey(item);
      if (!key) {
continue;
}
      if (currentDuplicateKeys.has(key)) {
continue;
}

      const dup = this.getDuplicateOf(item);
      if (!dup) {
continue;
}

      // Mark as seen and notify once per pair
      currentDuplicateKeys.add(key);
      if (this.notifiedDuplicateKeys.has(key)) {
continue;
}
      this.notifiedDuplicateKeys.add(key);

      // this.notificationService.warning(
      //   `Este producto ya se encuentra agregado en el carrito (${this.getDuplicateStatusName(item)}).`,
      //   'Producto duplicado'
      // );
    }

    // Prune keys that are no longer duplicates, so the user gets a fresh
    // warning if they remove one of the duplicates and re-add it later.
    for (const oldKey of Array.from(this.notifiedDuplicateKeys)) {
      if (!currentDuplicateKeys.has(oldKey)) {
        this.notifiedDuplicateKeys.delete(oldKey);
      }
    }
  }

  buildCartCities(): void {
    if (!this._actualItinerary || !this._actualItinerary.destination) {
      this.cities = [];
      this.insuranceItems = [];
      return;
    }

    // Insurance items - independientes de las ciudades
    this.insuranceItems = this._actualItinerary.items.filter(
      item => (item.categoryId === 10 || item.category_id === 10) && item.statusId !== 5
    );

    const hotels = this._actualItinerary.items.filter(item => item.categoryId === 1 || item.category_id === 1);
    const insurances = this._actualItinerary.items.filter(item => item.categoryId === 10 || item.category_id === 10);
    const otherCategories = this._actualItinerary.items.filter( item => item.categoryId !== 1 && item.category_id !== 1 && item.categoryId !== 10 && item.category_id !== 10);

// --- Transport grouping (ida + vuelta) ---
    const transports = otherCategories.filter(item => item.categoryId === 8 || item.category_id === 8);
    // --- Ticket grouping (subCategoryId === 9) ---
    const tickets = otherCategories.filter(item => item.subCategoryId === 9 || item.subCategoryId === 9);
    const nonTransportOther = otherCategories.filter(item =>
      (item.categoryId !== 8 && item.category_id !== 8) &&
      (item.subCategoryId !== 9)
    );

    const groupedTransports = transports.reduce((acc, item) => {
      const groupKey = `${item.providerId}-${item.productId}`;
      const statusId = item.statusId || item.status_id;
      const statusName = item.status || item.statusName || item.status_name;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          providerId: item.providerId,
          productId: item.productId,
          categoryId: 8,
          categoryName: 'TRANSPORTS',
          title: item.title,
          logo: item.logo,
          images: item.images,
          vehicle: item.vehicle,
          vehicleCode: item.vehicleCode,
          transferType: item.transferType,
          transferName: item.transferName,
          currency: item.currency,
          paxAdults: item.paxAdults,
          paxChildren: item.paxChildren,
          netPriceAgent: item.netPriceAgent || item.netPriceProf,
          netPriceProf: item.netPriceProf,
          netPrice: item.netPrice,
          totalPrice: item.totalPrice,
          isPaidAgency: item.isPaidAgency,
          isPaidProvider: item.isPaidProvider,
          blickoins: 0,
          creditBlickoins: 0,
          statusIds: [],
          transfers: [],
          // For city matching & base-item
          destination: item.destination,
          origin: item.origin,
          payloadUpdate: item.payloadUpdate,
          convertedAmounts: item.convertedAmounts,
          pricing: item.pricing,
          rateClass: item.rateClass,
          rateClassId: item.rateClassId,
        };
      }

      acc[groupKey].transfers.push(item);
      acc[groupKey].blickoins += item.blickoins || 0;
      acc[groupKey].creditBlickoins += item.creditBlickoins || 0;

      // Override main data from outbound leg (firstWay === 1)
      if (item.firstWay === 1) {
        acc[groupKey].netPriceAgent = item.netPriceAgent || item.netPriceProf;
        acc[groupKey].netPriceProf = item.netPriceProf;
        acc[groupKey].netPrice = item.netPrice;
        acc[groupKey].totalPrice = item.totalPrice;
        acc[groupKey].currency = item.currency;
        acc[groupKey].isPaidAgency = item.isPaidAgency;
        acc[groupKey].isPaidProvider = item.isPaidProvider;
        acc[groupKey].destination = item.destination;
        acc[groupKey].origin = item.origin;
        acc[groupKey].payloadUpdate = item.payloadUpdate;
        acc[groupKey].convertedAmounts = item.convertedAmounts;
        acc[groupKey].pricing = item.pricing;
        acc[groupKey].rateClass = item.rateClass;
        acc[groupKey].rateClassId = item.rateClassId;
      }

      // Order transfers by arrivalDate + pickup time so each (firstWay=1,
      // firstWay=2) pair appears in natural (ida, vuelta) order regardless
      // of CartItem.id ordering. This protects against backend inserts that
      // interleave ids between ida and vuelta of the same round-trip.
      acc[groupKey].transfers.sort((a, b) => {
        const aDate = a.arrivalDate || '';
        const bDate = b.arrivalDate || '';
        if (aDate !== bDate) {
          return aDate.localeCompare(bDate);
        }
        // Same date → order by pickup time. Prefer `formTransport.firstTime`
        // (already in HH:mm) and fall back to the ISO `firstArrivalDate` so
        // a missing/empty firstTime still yields a deterministic order.
        const aTime =
          a.formTransport?.firstTime ||
          (a.formTransport?.firstArrivalDate
            ? a.formTransport.firstArrivalDate.substring(11, 16)
            : '');
        const bTime =
          b.formTransport?.firstTime ||
          (b.formTransport?.firstArrivalDate
            ? b.formTransport.firstArrivalDate.substring(11, 16)
            : '');
        if (aTime !== bTime) {
          return aTime.localeCompare(bTime);
        }
        // Final tie-breaker: smaller id first.
        return (a.id || 0) - (b.id || 0);
      });

      // Avoid duplicate statuses
      if (!acc[groupKey].statusIds.some((s: any) => s.statusId === statusId)) {
        acc[groupKey].statusIds.push({ statusId, statusName });
      }

      return acc;
    }, {});

    const groupedTransportsArray = Object.values(groupedTransports);

    // --- Ticket grouping (subCategoryId === 9) ---
    const groupedTickets = tickets.reduce((acc, item) => {
      const groupKey = `${item.providerId}-${item.productId}`;
      const statusId = item.statusId || item.status_id;
      const statusName = item.status || item.statusName || item.status_name;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          providerId: item.providerId,
          productId: item.productId,
          categoryId: item.categoryId || item.category_id,
          subCategoryId: item.subCategoryId,
          categoryName: item.categoryName,
          subCategoryName: item.subCategoryName,
          title: item.title,
          logo: item.logo,
          images: item.images,
          currency: item.currency,
          paxAdults: item.paxAdults,
          paxChildren: item.paxChildren,
          netPriceAgent: item.netPriceAgent || item.netPriceProf,
          netPriceProf: item.netPriceProf,
          netPrice: item.netPrice,
          totalPrice: item.totalPrice,
          isPaidAgency: item.isPaidAgency,
          isPaidProvider: item.isPaidProvider,
          blickoins: 0,
          creditBlickoins: 0,
          statusIds: [],
          ticketItems: [],
          // For city matching & base-item
          destination: item.destination,
          origin: item.origin,
          payloadUpdate: item.payloadUpdate,
          convertedAmounts: item.convertedAmounts,
          pricing: item.pricing,
          rateClass: item.rateClass,
          rateClassId: item.rateClassId,
          selectDay: item.selectDay,
          selectHour: item.selectHour,
          adults: item.adults,
          childrens: item.childrens,
          form: item.form,
          ticket: item.ticket,
          description: item.description,
        };
      }

      acc[groupKey].ticketItems.push(item);
      acc[groupKey].blickoins += item.blickoins || 0;
      acc[groupKey].creditBlickoins += item.creditBlickoins || 0;

      // Avoid duplicate statuses
      if (!acc[groupKey].statusIds.some((s: any) => s.statusId === statusId)) {
        acc[groupKey].statusIds.push({ statusId, statusName });
      }

      return acc;
    }, {});

    const groupedTicketsArray = Object.values(groupedTickets);

    const groupedInsurances = insurances.reduce((acc, item) => {
      const location = item.destination?.iata_code;
      const arrival = item.arrivalDate;
      const departure = item.departureDate;

      if(!acc[location]) {
        acc[location] = {
          city: location,
          dd: arrival ? arrival.substring(8, 10) : '',
          mm: arrival ? arrival.substring(5, 7) : '',
          yy: arrival ? arrival.substring(2, 4) : '',
          ddd: arrival ? arrival.substring(8, 10) : '',
          mmm: arrival ? arrival.substring(5, 7) : '',
          yyy: arrival ? arrival.substring(2, 4) : '',
          items: [{
            arrivalDate: arrival,
            departureDate: departure,
            items: []
          }],
        };
      }

      acc[location].items[0].items.push({...item, statusId: item.status_id, categoryId: item.category_id || item.categoryId});

      return acc;
    }, {});

    const groupedInsurancesArray: CitySection[] = Object.values(groupedInsurances);

    const groupedHotels = hotels.reduce((acc, item) => {
      // Group by title (normalized) instead of hotelCode, because different providers
      // assign different hotelCodes to the same physical hotel (e.g. "525" vs "8473727")
      const groupKey = (item.title || '').trim().toLowerCase() || item.room?.hotelCode || 'unknown';
      const statusId = item.statusId || item.status_id;
      const statusName = item.status || item.statusName || item.status_name;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          hotelCode: item.room?.hotelCode || groupKey,
          categoryId: item.categoryId || item.category_id,
          categoryName: item.categoryName || item.category_name,
          destination: item.destination,
          form: item.form,
          origin: item.origin,
          title: item.title,
          statusIds: [],
          location: item.location,
          rooms: [],
          categoryHotel: item.categoryHotel,
          blickoins: item.blickoins,
          creditBlickoins: item.creditBlickoins,

          // nuevos
          totalRooms: 0,
          totalPrice: 0,
        };
      }

      acc[groupKey].rooms.push(item);

      // contar rooms
      acc[groupKey].totalRooms += 1;

      // calcular precio room
      let roomPrice = 0;

      if (statusId !== 5) {
        // Impuestos no incluidos (a pagar en destino) — solo para mostrar, no viene de la API
        // como parte de netPriceAgent/convertedAmounts. Vive en item.room.pricing, no en item
        // directo. Se suma ANTES de convertir moneda (currency.transform), no después.
        const taxesExcluded = item?.room?.pricing?.taxesExcluded || 0;
        if (item?.isPaidAgency || item?.isPaidProvider) {
          roomPrice =
            (item?.convertedAmounts?.netPriceAgent ||
            item?.netPrice ||
            0) + taxesExcluded;
        } else {
          roomPrice =
            this.currency.transform(
              (item?.netPriceAgent || 0) + taxesExcluded,
              item?.currency,
              this.currencyAgency?.code,
              this.currencies
            ) || 0;
        }
      }

      // acumular total hotel
      acc[groupKey].totalPrice += roomPrice;

      // evitar duplicados
      if (
        !acc[groupKey].statusIds.some(
          (s: any) => s.statusId === statusId
        )
      ) {
        acc[groupKey].statusIds.push({
          statusId,
          statusName,
        });
      }

      return acc;
    }, {});

    const groupedHotelsArray = Object.values(groupedHotels);

    this.cities = this._actualItinerary.items.length ? this._actualItinerary.destination
      .filter((dest: any) => dest.destination_info)
      .map((dest: any) => {
        const cityName = dest.destination_info.cityName;
        const arrival = dest.destination_info.arrival || dest.arrivalDate || '';
        const departure = dest.destination_info.departure || dest.departureDate || '';

        return {
          city: cityName,
          dd: arrival ? arrival.substring(8, 10) : '',
          mm: arrival ? arrival.substring(5, 7) : '',
          yy: arrival ? arrival.substring(2, 4) : '',
          ddd: arrival ? arrival.substring(8, 10) : '',
          mmm: arrival ? arrival.substring(5, 7) : '',
          yyy: arrival ? arrival.substring(2, 4) : '',
          items: [{
            arrivalDate: arrival,
            departureDate: departure,
            items: [...nonTransportOther, ...groupedTransportsArray, ...groupedTicketsArray, ...groupedHotelsArray]
              .filter((item: any) => {
                // Agrupar por city_name como el original (itinerary-sidebar)
                const categoryId = item.categoryId || item.category_id;
                const itemDest = item.destination?.iata_code || item.payloadUpdate?.products?.[0]?.destination || '';

                if (categoryId === 1) {
                  // Hotels
                  const itemCityName = item.form?.destination?.city_name;
                  return itemCityName === cityName || itemDest === dest.iata_code;
                }

                if (categoryId === 8) {
                  // Transports
                  const itemCityName = item.payloadUpdate?.transfers?.pickupLocation?.type !== 'airport'
                    ? item.origin?.city_name
                    : item.origin?.name;
                  return itemCityName === cityName || itemDest === dest.iata_code;
                }
                if (categoryId === 2) {
                  // Rent a car - match by pickup location (origin)
                  // Fallback chain: form.origin.city_name → form.origin.name → iata_code
                  const originCityName = item.form?.origin?.city_name || item.form?.origin?.name;
                  return originCityName === cityName || itemDest === dest.iata_code;
                }
                if (categoryId === 6 || categoryId === 9) {
                  // Tours / Tickets
                  const itemCityName = item.form?.destination?.city_name;
                  return itemCityName === cityName || itemDest === dest.iata_code;
                }
                return itemDest === dest.iata_code;
              })
              .map((item: any) => ({
                ...item,
                categoryId: item.categoryId || item.category_id,
                statusId: item.statusId || item.status_id,
              }))
          }]
        };
      })
      .filter((city: CitySection) => city.city) : [];

    this.cities = [...this.cities, ...groupedInsurancesArray];
  }

  removeItem(item: any): void {
    const categoryId = item.categoryId || item.category_id;

    // Handle grouped ticket items
    if (item.ticketItems && item.ticketItems.length > 0) {
      this.removeGroupedTickets(item);
      return;
    }

    // Handle transport paired items (round-trip transfers)
    if (categoryId === 8 && item?.firstWay) {
      this.removeTransportPair(item);
      return;
    }

    // Branch: if the item is "Reservado" (statusId 2) we cancel the reservation
    // (POST /products/cancelReservation) instead of deleting it. This matches
    // the behavior of the itinerary-summary view.
    if (this.isItemReserved(item)) {
      this.cancelReservedItem(item);
      return;
    }

    // Show confirmation modal for single item removal
    const initialState: ModalOptions = {
      initialState: {
        title: 'Eliminar',
        message: '¿Esta seguro de eliminar <br /> el producto?',
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    const modalConfig: any = {
      ...initialState,
      ...this.config,
    };

    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const confirmSubscription = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {

        if(item.categoryId === 1) {
          // Hotel deletion: remove all rooms, call updateItinerary only ONCE at the end
          const rooms = item.rooms || [];

          // Mark all rooms as loading
          rooms.forEach((room: any) => this.markItemLoading(room));

          // Update pax counts for all rooms combined
          if (this._actualItinerary?.destination) {
            rooms.forEach((room: any) => {
              const paxesQty = {
                destination: room.destination?.iata_code,
                adults: Number(room.paxAdults),
                children: Number(room.paxChildren)
              };
              this._actualItinerary.destination.forEach((destination: any) => {
                if (destination.iata_code === paxesQty.destination) {
                  destination.destination_info.adults -= paxesQty.adults;
                  destination.destination_info.childs -= paxesQty.children;
                }
              });
            });
          }

          // Call hotelService to remove rooms from hotel modal state
          rooms.forEach((room: any) => {
            this.hotelService.removeSelectedRoom(room);
          });

          // Make API calls for each room deletion (skip updateItinerary per call)
          const remove$ = rooms.map((r: any) => {
            const roomToRemove = r.item !== undefined ? r.item : r;
            return this.itineraryService.remove$(roomToRemove, null, true);
          });

          // After ALL deletions complete, update itinerary ONCE
          concat(...remove$).pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            complete: () => {
              const clientDataStr = localStorage.getItem('setting');
              if (clientDataStr) {
                const clientData = JSON.parse(clientDataStr);
                this.itineraryService.itinerary.clientId = clientData.id;
              }
              this.itineraryService.updateItinerary(this.itineraryService.itinerary);
              this.itineraryService.userService.itineraryUpdateProductsTotalPrices(
                this.itineraryService.userService.itineraryActive[0]?.id
              );
            },
            error: () => {
              this.notificationService.error('Error !!', 'There\'s been a problem');
            }
          });
        } else {
          this.executeRemove(item);
        }
      }

      confirmSubscription.unsubscribe();
    });
  }

  private cancelReservedItem(item: any): void {
    const initialState: ModalOptions = {
      initialState: {
        title: 'Cancelar',
        message: '¿Esta seguro de cancelar <br /> el producto?',
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    const modalConfig: any = {
      ...initialState,
      ...this.config,
    };

    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const confirmSubscription = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        const target = item.categoryId === 1
          ? (item.rooms && item.rooms.length > 0 ? item.rooms[0] : item)
          : item;

        const unwrapped = (target && target.item !== undefined) ? target.item : target;

        this.markItemLoading(item);

        const loaderRef: MatDialogRef<CancelModalComponent> = this.dialog.open(
          CancelModalComponent,
          {
            width: '420px',
            disableClose: true,
            panelClass: 'cancel-loader-dialog',
          }
        );

        this.itineraryService.cancelReservationFromCart(
          unwrapped,
          () => {
            if (loaderRef && loaderRef.close) {
              loaderRef.close();
            }

            this.loadingItemIds.delete(this.getItemTrackId(item));
            this.buildCartCities();
            this.cdr.detectChanges();
            this.notificationService.success('Su producto fue cancelado con éxito');
          }
        );
      }

      confirmSubscription.unsubscribe();
    });
  }

  private removeTransportPair(item: any): void {
    if (this.isItemReserved(item)) {
      this.cancelReservedItem(item);
      return;
    }

    const initialState: ModalOptions = {
      initialState: {
        icon: 'assets/icons/itinerary-detail/04-01.svg',
        title: 'Eliminar',
        message: 'Esta acción eliminará los traslados de ida y vuelta <br /> ¿Está seguro de eliminar los productos?'
      }
    };

    const modalConfig: any = {
      ...initialState,
      ...this.config,
    };

    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const confirmSubscription = this.bsModalRef.content.confirmResult.subscribe(async (result: boolean) => {
      if (result) {
        const pairedItems = this.findPairedItems(item, this._actualItinerary?.items || []);

        if (pairedItems.length === 2) {
          // Sequence the two deletes so the backend finishes the first
          // DELETE+PUT roundtrip before we send the second. Otherwise the
          // second PUT re-sends items the first PUT already removed, and the
          // backend rejects with "Duplicate entry ... for key product_key_unique".
          await this.processItem(pairedItems[0], item.currency);
          await this.processItem(pairedItems[1], item.currency);
        }
      }
      confirmSubscription.unsubscribe();
    });
  }

  private async processItem(item: any, currency: string): Promise<void> {
    // Mark item as loading (skeleton) before removal
    this.markItemLoading(item);

    // Update pax counts in destinations
    const paxesQty = {
      destination: item.destination?.iata_code,
      adults: Number(item.paxAdults),
      children: Number(item.paxChildren)
    };

    if (this._actualItinerary?.destination) {
      this._actualItinerary.destination.forEach((destination: any) => {
        if (destination.iata_code === paxesQty.destination) {
          destination.destination_info.adults -= paxesQty.adults;
          destination.destination_info.childs -= paxesQty.children;
        }
      });
    }

    // Emit events
    this.itineraryService.currencyRemoveEmit.next(currency);

    // Remove vehicle from transport service
    this.transportsService.removeVehicle(item);

    // Use remove$ so the DELETE roundtrip (including the backend's
    // updateItinerary that re-emits _actualItinerary) finishes BEFORE the
    // next processItem call starts. The previous implementation listened to
    // the shared `deleteItem` BehaviorSubject with take(1), which races
    // when two deletes are issued back-to-back: only the first await
    // resolved and the second transfer was never sent to the backend.
    const itemToRemove = item.item !== undefined ? item.item : item;
    await this.itineraryService
      .remove$(itemToRemove, null, true)
      .pipe(take(1))
      .toPromise();

    // Note: NOT emitting itemRemove here because itineraryService.remove()
    // already handles the toast notification. Emitting would cause a duplicate
    // call to onItemRemove() in the parent component, which calls remove() again.
  }

  private findPairedItems(mainItem: any, allItems: any[]): any[] {
    const pairedItems: any[] = [mainItem];

    if (mainItem.firstWay === 1) {
      const matchingItem = allItems.find(otherItem =>
        otherItem !== mainItem &&
        otherItem.firstWay === 2 &&
        otherItem.vehicleCode === mainItem.vehicleCode
      );
      if (matchingItem) {
        pairedItems.push(matchingItem);
      }
    } else if (mainItem.firstWay === 2) {
      const matchingItem = allItems.find(otherItem =>
        otherItem !== mainItem &&
        otherItem.firstWay === 1 &&
        otherItem.vehicleCode === mainItem.vehicleCode
      );
      if (matchingItem) {
        pairedItems.unshift(matchingItem);
      }
    }

    return pairedItems;
  }

  private removeGroupedTickets(item: any): void {
    const hasMultiple = item.ticketItems.length > 1;
    const hasReserved = item.ticketItems.some((t: any) => (t.statusId || t.status_id) === 2);

    const initialState: ModalOptions = {
      initialState: {
        icon: 'assets/icons/itinerary-detail/04-01.svg',
        title: hasReserved ? 'Cancelar' : 'Eliminar',
        message: hasMultiple
          ? 'Esta acción eliminará todos los tickets del grupo <br /> ¿Está seguro de eliminar los productos?'
          : (hasReserved ? '¿Esta seguro de cancelar <br /> el producto?' : '¿Esta seguro de eliminar <br /> el producto?')
      }
    };

    const modalConfig: any = {
      ...initialState,
      ...this.config,
    };

    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const confirmSubscription = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        if (hasReserved) {
          const loaderRef: MatDialogRef<CancelModalComponent> = this.dialog.open(
            CancelModalComponent,
            {
              width: '420px',
              disableClose: true,
              panelClass: 'cancel-loader-dialog',
            }
          );

          let pending = item.ticketItems.length;
          item.ticketItems.forEach((ticket: any) => {
            this.markItemLoading(ticket);
            this.itineraryService.cancelReservationFromCart(
              ticket,
              () => {
                pending--;
                if (pending === 0) {
                  this.loadingItemIds.delete(this.getItemTrackId(item));
                  this.buildCartCities();
                  this.cdr.detectChanges();
                  if (loaderRef && loaderRef.close) {
                    loaderRef.close();
                  }
                  this.notificationService.success('Su producto fue cancelado con éxito');
                }
              }
            );
          });
        } else {
          item.ticketItems.forEach((ticket: any, index: number) => {
            if (index > 0) {
              setTimeout(() => this.executeRemove(ticket), 500);
            } else {
              this.executeRemove(ticket);
            }
          });
        }
      }
      confirmSubscription.unsubscribe();
    });
  }

  private executeRemove(item: any): void {
    const categoryId = item.categoryId || item.category_id;

    // Mark item as loading (skeleton) before removal
    this.markItemLoading(item);

    // Update pax counts in destinations
    if (this._actualItinerary?.destination) {
      const paxesQty = {
        destination: item.destination?.iata_code,
        adults: Number(item.paxAdults),
        children: Number(item.paxChildren)
      };

      this._actualItinerary.destination.forEach((destination: any) => {
        if (destination.iata_code === paxesQty.destination) {
          destination.destination_info.adults -= paxesQty.adults;
          destination.destination_info.childs -= paxesQty.children;
        }
      });
    }

    // Call service to remove hotel room
    if (categoryId === 1) {
      this.hotelService.removeSelectedRoom(item);
    }

    if (categoryId === 8 && item?.vehicle?.payload?.[0]?.transfers?.paxList) {
      this.transportsService.removeVehicle(item);
    }

    // Remove from itinerary via ItineraryService
    this.itineraryService.remove(item.item !== undefined ? item.item : item, null);

    // Note: NOT emitting itemRemove here because itineraryService.remove()
    // already handles the toast notification. Emitting would cause a duplicate
    // call to onItemRemove() in the parent component, which calls remove() again.
  }

  /**
   * Execute remove and return an Observable that completes when deletion is done.
   * Uses the new remove$ method which returns an Observable.
   */
  private executeRemove$(item: any): Observable<void> {
    const categoryId = item.categoryId || item.category_id;
    // Mark item as loading (skeleton) before removal
    this.markItemLoading(item);

    // Update pax counts in destinations
    if (this._actualItinerary?.destination) {
      const paxesQty = {
        destination: item.destination?.iata_code,
        adults: Number(item.paxAdults),
        children: Number(item.paxChildren)
      };

      this._actualItinerary.destination.forEach((destination: any) => {
        if (destination.iata_code === paxesQty.destination) {
          destination.destination_info.adults -= paxesQty.adults;
          destination.destination_info.childs -= paxesQty.children;
        }
      });
    }

    // Call service to remove hotel room
    if (categoryId === 1) {
      this.hotelService.removeSelectedRoom(item);
    }

    if (categoryId === 8 && item?.vehicle?.payload?.[0]?.transfers?.paxList) {
      this.transportsService.removeVehicle(item);
    }

    // Same unwrapping as executeRemove - use item.item if it exists
    const itemToRemove = item.item !== undefined ? item.item : item;
    return this.itineraryService.remove$(itemToRemove).pipe(
      map(() => undefined as void)
    );
  }

  /**
   * Handles per-transfer remove/cancel events emitted by usb-transport-item.
   * Status 1 (Cotización): if sibling is also status 1 → remove both; otherwise remove just this one.
   * Status 2 (Reservado): cancel just this transfer.
   * Status 3/5: button is not rendered, but we bail safely.
   */
  onRemoveTransfer(payload: { item: any; transfer: any }): void {

    const transfer = payload?.transfer ?? payload;
    const status = transfer?.statusId ?? transfer?.status_id ?? transfer?.statusIds?.[0]?.statusId;

    if (status === 2) {
      this.cancelSingleTransfer(transfer);
      return;
    }

    if (status === 1) {
      const sibling = (payload?.item?.transfers || []).find(
        (t: any) => t !== transfer
      );

      const siblingStatus = sibling ? (sibling.statusId ?? sibling.status_id) : null;

      if (sibling && siblingStatus === 1) {

        this.confirmAndRemoveGroup([transfer, sibling]);
      } else {
        this.confirmAndRemoveSingle(transfer);
        this.transportsService.removeVehicle(transfer.transfers[0]);

      }
      return;
    }
  }

  private cancelSingleTransfer(transfer: any): void {
    const initialState: ModalOptions = {
      initialState: {
        title: 'Cancelar',
        message: '¿Está seguro de cancelar este traslado?',
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    const modalConfig: any = { ...initialState, ...this.config };
    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const sub = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        const loaderRef: MatDialogRef<CancelModalComponent> = this.dialog.open(
          CancelModalComponent,
          { width: '420px', disableClose: true, panelClass: 'cancel-loader-dialog' }
        );

        const leaf: any = Array.isArray(transfer?.transfers) && transfer.transfers.length > 0
          ? transfer.transfers[0]
          : transfer;

        const originalItem = (this.itineraryService.itinerary?.items || []).find(
          (it: any) => it.id === leaf.id
        );

        const transferWithContext: any = {
          ...(originalItem || leaf),
          itinerary_id:
            (originalItem && originalItem.itinerary_id) ||
            leaf.itinerary_id ||
            this.itineraryService.itinerary?.id,
        };

        this.markItemLoading(transfer);
        this.itineraryService.cancelReservationFromCart(transferWithContext, () => {
          this.loadingItemIds.delete(this.getItemTrackId(transfer));

          if (this.itineraryService.itinerary?.items && Array.isArray(this._actualItinerary?.items)) {
            this._actualItinerary.items = [...this.itineraryService.itinerary.items];
          }

          const cancelledId = leaf?.id;
          if (cancelledId != null && Array.isArray(this._actualItinerary?.items)) {
            const target = this._actualItinerary.items.find(
              (it: any) => Number(it?.id) === Number(cancelledId)
            );
            if (target) {
              target.status = 'Cancelado';
              target.statusName = 'Cancelado';
              if (Array.isArray(target.statusIds) && target.statusIds.length > 0) {
                target.statusIds = target.statusIds.map((s: any) => ({
                  ...s,
                  statusId: 5,
                  statusName: 'Cancelado',
                }));
              }
            }
          }

          this.buildCartCities();
          this.cdr.detectChanges();
          if (loaderRef?.close) {
loaderRef.close();
}
          this.notificationService.success('Su producto fue cancelado con éxito');
        });
      }
      sub.unsubscribe();
    });
  }

  private confirmAndRemoveGroup(transfers: any[]): void {
    const initialState: ModalOptions = {
      initialState: {
        title: 'Eliminar',
        message: 'Esta acción eliminará los traslados de ida y vuelta. ¿Está seguro?',
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    const modalConfig: any = { ...initialState, ...this.config };
    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const sub = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        let pending = transfers.length;
        let errored = false;

          transfers.forEach((transfer: any) => {
            if (transfer?.payload?.[0]?.categoriesId[0]) {
                this.transportsService.removeVehicle(transfer);
            }
        });

        const onOneDone = () => {
          if (--pending === 0) {
            this.loadingItemIds.delete(this.getItemTrackId(transfers[0]));
            this.buildCartCities();
            this.cdr.detectChanges();
            if (errored) {
              this.notificationService.error('Error', 'No se pudieron eliminar todos los traslados');
            }
          }
        };

        this.markItemLoading(transfers[0]);
        this.executeRemove$(transfers[0]).subscribe({
          complete: onOneDone,
          error: () => {
 errored = true; onOneDone();
}
        });

        this.markItemLoading(transfers[1]);
        this.executeRemove$(transfers[1]).subscribe({
          complete: onOneDone,
          error: () => {
 errored = true; onOneDone();
}
        });
      }
      sub.unsubscribe();
    });
  }

  private confirmAndRemoveSingle(transfer: any): void {
    const initialState: ModalOptions = {
      initialState: {
        title: 'Eliminar',
        message: '¿Está seguro de eliminar este traslado?',
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    const modalConfig: any = { ...initialState, ...this.config };
    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);

    const sub = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        const leaf: any = Array.isArray(transfer?.transfers) && transfer.transfers.length > 0
          ? transfer.transfers[0]
          : transfer;

        this.markItemLoading(transfer);
        this.executeRemove$(leaf).subscribe({
          complete: () => {
            this.loadingItemIds.delete(this.getItemTrackId(transfer));
            this.buildCartCities();
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.loadingItemIds.delete(this.getItemTrackId(transfer));
            this.cdr.detectChanges();
            this.notificationService.error('Error', 'No se pudo eliminar el producto');
          }
        });
      }
      sub.unsubscribe();
    });
  }

  onRemoveItem(item: any): void {
    this.removeItem(item);
  }

  onRemoveRoom(room: any): void {
    this.removeItem(room);
  }

  onOpenHotelMap(item: any): void {
    // Transform item to hotel format expected by ModalHotelComponent
    const hotelData = this.transformItemToHotel(item);

    this.dialog.open(ModalsComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '1240px',
      panelClass: 'custom-container-transport',
      disableClose: true,
      data: {
        product: hotelData,
        origin: 'HOTELS',
        tabActive: 'maps',
        quote: false,
        action: 'view',
        clientSetting: this.getClientSetting(),
        currencyAgency: this.currencyAgency,
        currencies: this.currencies,
        productQty: 1,
        form: item.form,
        payloadUpdate: item.payloadUpdate,
        payload: item.payloadOriginal,
        arrivalDate: item.arrivalDate,
        departureDate: item.departureDate,
        view_info: true
      }
    });
  }

private transformItemToHotel(item: any): any {
    // The grouped cart item loses hotelAdditionalInfo and payloadOriginal when
    // buildCartCities() flattens rooms. The raw backend item lives inside
    // item.rooms[0] and carries the full shape that ModalHotelComponent needs.
    const source = item?.rooms?.[0] ?? item;
    const additionalInfo = source.hotelAdditionalInfo?.[0] || {};

    return {
      id: source.id,
      title: item.title || source.title,
      coordinates: {
        latitude: additionalInfo.howtoget?.lat,
        longitude: additionalInfo.howtoget?.long
      },
      additionalInfo: {
        location: item.location || source.location,
        starRating: item.categoryHotel || source.categoryHotel,
        images: additionalInfo.images || [],
        howtoget: additionalInfo.howtoget,
        amenities: additionalInfo.amenities || [],
        rooms: additionalInfo.rooms || [],
        TimeInformation: source.checkTimes || { checkIn: null, checkOut: null }
      },
      dateIni: source.arrivalDate,
      dateEnd: source.departureDate,
      origin: 'HOTELS',
      searchId: source.searchId,
      description: source.description,
      form: item.form || source.form,
      payloadOriginal: source.payloadOriginal,
      hotelAdditionalInfo: source.hotelAdditionalInfo,
      // Prices/currency live on the raw backend item (and on source.room),
      // not always on additionalInfo.rooms. Expose them at the hotel root so
      // ModalHotelComponent.generateCoordinates() can use them as fallback.
      netPriceProf: source.netPriceProf ?? source.room?.netPriceProf,
      optionNightsNetTotalProf: source.optionNightsNetTotalProf ?? source.room?.optionNightsNetTotalProf,
      currency: source.currency ?? source.room?.pricing?.currency,
      cancellation_policy: source.cancellation_policy ?? source.room?.cancellationPolicy,
      boardName: source.boardName ?? source.room?.boardName,
      rateClass: source.rateClass ?? source.room?.rateClass,
    };
  }

  private getClientSetting(): any {
    try {
      const settingStr = localStorage.getItem('setting') || localStorage.getItem('clients_setting_app_booking');
      return settingStr ? JSON.parse(settingStr) : {};
    } catch {
      return {};
    }
  }

  isRemovable(item: any): boolean {
    // Hotels (grouped): allow status 1 (Cotizado → trash) and 2 (Reservado → cancel).
    if(item.statusIds && item.statusIds.length) {
      if(item?.statusIds?.find(s => (s.statusId !== 1 && s.statusId !== 2))) {
        return false;
      }

      return true;
    }

    // Grouped tickets: allow at least one ticket in status 1 or 2.
    if (item.ticketItems && item.ticketItems.length) {
      return item.ticketItems.some((t: any) => {
        const sid = t.statusId ?? t.status_id;
        return sid === 1 || sid === 2;
      });
    }

    const sid = item?.statusId ?? item?.status_id;
    return sid === 1 || sid === 2;
  }

  /**
   * Returns true when the item is in "Reservado" (statusId 2) status and
   * therefore the action button should show the cancel icon and trigger
   * `cancelReservation` instead of `executeRemove`.
   *
   * Mirrors the same dual-format detection as isRemovable:
   *  - Hotels grouped: every room in `statusIds` is 2.
   *  - Standalone items: `statusId === 2`.
   */
  isItemReserved(item: any): boolean {
    if (!item) {
      return false;
    }

    if (item.statusIds && item.statusIds.length > 0) {
      return item.statusIds.every((s: any) => s?.statusId === 2);
    }

    return (item.statusId ?? item.status_id) === 2;
  }

  /** Returns true only if the delete button should be shown for this item.
   * Hides the button when item is part of a group with 2+ items (transports, tickets, hotels). */
  canShowDeleteButton(item: any): boolean {
    if (!this.isRemovable(item)) {
      return false;
    }

    // Grouped transports (ida + vuelta) - hide if 2+ transfers grouped
    if (item.transfers && item.transfers.length > 1) {
      return false;
    }

    // Grouped tickets - hide if 2+ tickets grouped
    if (item.ticketItems && item.ticketItems.length > 1) {
      return false;
    }

    // Grouped hotels - hide if 2+ rooms grouped under same hotel
    if (item.rooms && item.rooms.length > 1) {
      return false;
    }

    return true;
  }

  /** Generate a unique tracking ID for a cart item */
  private getItemTrackId(item: any): string {
    if (item.hotelCode) {
return `hotel-${item.hotelCode}`;
}
    if (item.transfers) {
return `transport-${item.providerId}-${item.productId}`;
}
    if (item.ticketItems) {
return `ticket-${item.providerId}-${item.productId}`;
}
    // Transport leaf (ida/vuelta individual): same key as the parent group so
    // the skeleton flag is shared with whatever markItemLoading ran on the
    // group, and the delete-side can clear it with the same key.
    const cat = item.categoryId || item.category_id;
    if (cat === 8 && item.providerId != null && item.productId != null) {
      return `transport-${item.providerId}-${item.productId}`;
    }
    return `${cat}-${item.id || item.uid || Math.random()}`;
  }

  /** Check if an item is currently in skeleton/loading state */
  isItemLoading(item: any): boolean {
    return this.loadingItemIds.has(this.getItemTrackId(item));
  }

  /** Mark an item as loading (skeleton) before removal */
  private markItemLoading(item: any): void {
    this.loadingItemIds.add(this.getItemTrackId(item));
    this.cdr.detectChanges();
  }

  getItemCategoryName(item: any): string {
    const subCategoryId = item.subCategoryId;

    if (subCategoryId === 9 || item.ticketItems) {
      return 'TICKET';
    }

    return item.categoryName;
  }

  getStatusClass(statusId: number): string {
    const statusMap: { [key: number]: string } = {
      1: 'product-status-saved',
      2: 'product-status-reserved',
      3: 'product-status-locked',
      4: 'product-status-denied',
      5: 'product-status-cancelled',
      6: 'product-status-paid-agency',
      7: 'product-status-paid-provider',
      8: 'product-status-completed'
    };
    return statusMap[statusId] || '';
  }

  getHeaderStyle(): any {
    return { 'background-color': this.styleSidebar?.background_color_header };
  }

  getTitleColorStyle(): any {
    return { color: this.styleSidebar?.title };
  }

  getBgColorStyle(): any {
    return { 'background-color': this.styleSidebar?.background_color };
  }

  getItemContainerStyle(idx: number, last: boolean): any {
    const bgColor = this.styleSidebar?.background_color;
    const styles: any = {
      'border-bottom-left-radius': '34px',
      'border-bottom-right-radius': '34px',
      'padding-bottom': '25px !important',
      'background-color': 'white',
      'border-radius': '6px',
    };
    if (idx !== 0 && !last) {
      styles['border-bottom'] = 'none';
    } else {
      styles['border-bottom'] = '2px solid ' + bgColor;
    }
    if (idx !== 0) {
      styles['border-top-left-radius'] = 'none';
    } else {
      styles['border-top-left-radius'] = '34px';
    }
    return styles;
  }

  getRemoveBtnStyle(): any {
    const bgColor = this.styleSidebar?.background_color;
    return {
      'border-bottom': '2px solid ' + bgColor,
      'border-left': '2px solid ' + bgColor,
    };
  }

  _redirectToHome(): void {
    if (this.currentCategory) {
      const tabForSearchBox = this.currentCategory === 'TRANSPORTS' ? 'TRANSPORTE'
        : this.currentCategory === 'TOURS' ? 'EXPERIENCIAS'
        : this.currentCategory;
      sessionStorage.setItem('home-tab-from-cart', tabForSearchBox);
    }
    this._router.navigateByUrl('/home');
  }

_redirectToSummary(): void {

  // if(!this.permissions['']) {
  //   this.notificationService.info('Usted no puede continuar debido a su perfil');
  //   return;
  // }

  if (!this.cities || this.cities.length === 0) {
    this.notificationService.warning('No hay productos en el carrito. Por favor, agregue al menos un producto para continuar.');
    return;
  }

  if (this.currentCategory === 'TRANSPORTS') {
    // ✅ Obtener pasajeros faltantes
    const currentInfoRemaining = this.transportsService.getRemainingPassengers();


    // ✅ Filtrar transportes cotizados (status_id === 1)
    const filterItemsTransport = this.itineraryService.itinerary.items.filter(
      c => c.category_id === 8 && c.status_id === 1
    );


    // ✅ Calcular total de pasajeros en transportes
    const totalAdultsInTransports = filterItemsTransport.reduce((acc, c) => acc + (c.paxAdults || 0), 0);
    const totalChildrenInTransports = filterItemsTransport.reduce((acc, c) => acc + (c.paxChildren || 0), 0);



    // ✅ Verificar si hay pasajeros faltantes
    const hasMissingAdults = totalAdultsInTransports < currentInfoRemaining.adults;
    const hasMissingChildren = totalChildrenInTransports < currentInfoRemaining.children;



    // ✅ Si faltan pasajeros, mostrar modal
    if (hasMissingAdults || hasMissingChildren) {
      const missingAdults = currentInfoRemaining.adults - totalAdultsInTransports;
      const missingChildren = currentInfoRemaining.children - totalChildrenInTransports;

      const initialState: ModalOptions = {
        initialState: {
          icon: 'assets/icons/itinerary-detail/ALERTA-01.svg',
          title: 'Importante',
          subtitle: '',
          message: `${currentInfoRemaining.adults} adulto${ currentInfoRemaining.adults !== 1 ? 's' : ''} y ${currentInfoRemaining.children} niño${currentInfoRemaining.children !== 1 ? 's' : ''} no han sido agregados a un traslado. ¿Desea continuar sin agregarlos?`,
        }
      };

      const modalOptions = Object.assign({}, initialState, this.config);
      this.bsModalRef = this.modalService.show(ConfirmationModalComponent, modalOptions);

      this.bsModalRef?.content.confirmResult.subscribe((result: boolean) => {
        if (result) {
          this._router.navigateByUrl('/summary');
        }
      });

      return;
    }

    // ✅ Todos los pasajeros tienen traslado
    this._router.navigateByUrl('/summary');
    return;
  }

  this._router.navigateByUrl('/summary');
}
}
